/**
 * HeroEditor — visual editor for `config.hero`.
 *
 * Schema reference (matches /site.config.json):
 *   hero: {
 *     eyebrow:      string,
 *     title:        string,
 *     subtitle:     string,
 *     ctaPrimary:   { label: string, href: string },
 *     ctaSecondary: { label: string, href: string },
 *     metrics:      [ { label: string, value: string }, ... ]
 *   }
 *
 * The editor mutates the slice in place via `onChange(next)`; the parent
 * (WebsiteSettings) holds the full payload and the dirty flag.
 *
 * Design notes
 *   • Live preview tile on the right mirrors how the Hero band actually
 *     renders on the public site, so admins can iterate copy without
 *     opening a second tab.
 *   • Metrics are reorderable (move up / down) and removable; we cap at 6
 *     because the layout grid won't lay out more than that elegantly.
 *   • All inputs are uncontrolled-via-prop ("controlled by parent state")
 *     — every keystroke commits to the parent so the dirty flag stays
 *     accurate and unsaved changes survive tab switching.
 */
import React, { useCallback } from 'react';
import {
  Card, Field, Input, Textarea, Button, Badge,
  COLOR, FONT, RADIUS,
} from '../../../components/ss';

// Local helpers — the shared <Section> component doesn't support a
// description or right-aligned action slot, so we render our own
// section header here and a small caption helper for field hints.
function SectionBlock({ title, description, action, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 12, marginBottom: 10,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: COLOR.text }}>{title}</div>
          {description && (
            <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 3, lineHeight: 1.4 }}>
              {description}
            </div>
          )}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

function FieldHint({ children }) {
  if (!children) return null;
  return (
    <div style={{ fontSize: 11, color: COLOR.textMuted, marginTop: 4, lineHeight: 1.4 }}>
      {children}
    </div>
  );
}

const MAX_METRICS = 6;

const EMPTY_HERO = {
  eyebrow: '',
  title: '',
  subtitle: '',
  ctaPrimary:   { label: '', href: '' },
  ctaSecondary: { label: '', href: '' },
  metrics: [],
};

// Coerce whatever the JSON had into the expected shape so we never blow up
// on a missing key when an older config is loaded.
function normalize(value) {
  const v = value || {};
  return {
    eyebrow:  typeof v.eyebrow  === 'string' ? v.eyebrow  : '',
    title:    typeof v.title    === 'string' ? v.title    : '',
    subtitle: typeof v.subtitle === 'string' ? v.subtitle : '',
    ctaPrimary:   {
      label: v?.ctaPrimary?.label ?? '',
      href:  v?.ctaPrimary?.href  ?? '',
    },
    ctaSecondary: {
      label: v?.ctaSecondary?.label ?? '',
      href:  v?.ctaSecondary?.href  ?? '',
    },
    metrics: Array.isArray(v.metrics)
      ? v.metrics.map(m => ({ label: m?.label ?? '', value: m?.value ?? '' }))
      : [],
  };
}

export default function HeroEditor({ value, onChange }) {
  const hero = normalize(value);

  // Single-field mutator (top-level scalars).
  const setField = useCallback((key, val) => {
    onChange({ ...hero, [key]: val });
  }, [hero, onChange]);

  // CTA button mutator — each CTA is an object with label + href.
  const setCta = useCallback((which, key, val) => {
    onChange({ ...hero, [which]: { ...hero[which], [key]: val } });
  }, [hero, onChange]);

  // Metrics — append, edit, remove, reorder.
  const addMetric = () => {
    if (hero.metrics.length >= MAX_METRICS) return;
    onChange({ ...hero, metrics: [...hero.metrics, { label: '', value: '' }] });
  };
  const setMetric = (idx, key, val) => {
    const next = hero.metrics.map((m, i) => i === idx ? { ...m, [key]: val } : m);
    onChange({ ...hero, metrics: next });
  };
  const removeMetric = (idx) => {
    onChange({ ...hero, metrics: hero.metrics.filter((_, i) => i !== idx) });
  };
  const moveMetric = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= hero.metrics.length) return;
    const next = [...hero.metrics];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...hero, metrics: next });
  };

  const resetSection = () => {
    if (window.confirm('Reset the Hero section to empty defaults? Unsaved edits to other sections are preserved.')) {
      onChange({ ...EMPTY_HERO });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 460px)',
      gap: 20,
      alignItems: 'flex-start',
    }}>
      {/* ── Form column ─────────────────────────────────────────────── */}
      <Card>
        <SectionBlock
          title="Hero band"
          description="The big banner at the top of the home page. Eyebrow → Title → Subtitle → buttons → metrics."
          action={<Button size="sm" variant="ghost" onClick={resetSection}>Reset section</Button>}
        >
          <Field label="Eyebrow">
            <Input
              value={hero.eyebrow}
              onChange={e => setField('eyebrow', e.target.value)}
              placeholder="Home health & personal care"
              maxLength={120}
            />
            <FieldHint>Small label above the title (e.g. category or audience).</FieldHint>
          </Field>

          <Field label="Title" required>
            <Input
              value={hero.title}
              onChange={e => setField('title', e.target.value)}
              placeholder="Care your loved one trusts. Right at home."
              maxLength={140}
            />
            <FieldHint>The big headline. Keep it under ~60 characters for best mobile rendering.</FieldHint>
          </Field>

          <Field label="Subtitle">
            <Textarea
              rows={4}
              value={hero.subtitle}
              onChange={e => setField('subtitle', e.target.value)}
              placeholder="Skilled nursing, therapy, and personal care delivered by compassionate caregivers…"
              maxLength={500}
            />
            <FieldHint>Supporting paragraph beneath the title.</FieldHint>
          </Field>

          {/* CTA buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6 }}>
            <fieldset style={fsStyle}>
              <legend style={legendStyle}>Primary button</legend>
              <Field label="Label">
                <Input
                  value={hero.ctaPrimary.label}
                  onChange={e => setCta('ctaPrimary', 'label', e.target.value)}
                  placeholder="Schedule a free assessment"
                  maxLength={60}
                />
              </Field>
              <Field label="Link">
                <Input
                  value={hero.ctaPrimary.href}
                  onChange={e => setCta('ctaPrimary', 'href', e.target.value)}
                  placeholder="/contact"
                />
              </Field>
            </fieldset>

            <fieldset style={fsStyle}>
              <legend style={legendStyle}>Secondary button</legend>
              <Field label="Label">
                <Input
                  value={hero.ctaSecondary.label}
                  onChange={e => setCta('ctaSecondary', 'label', e.target.value)}
                  placeholder="Our services"
                  maxLength={60}
                />
              </Field>
              <Field label="Link">
                <Input
                  value={hero.ctaSecondary.href}
                  onChange={e => setCta('ctaSecondary', 'href', e.target.value)}
                  placeholder="/home#services"
                />
              </Field>
            </fieldset>
          </div>

          {/* Metrics */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: COLOR.text }}>Metric chips</div>
                <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
                  Trust signals shown as a horizontal row beneath the buttons. Up to {MAX_METRICS}.
                </div>
              </div>
              <Button
                size="sm"
                onClick={addMetric}
                disabled={hero.metrics.length >= MAX_METRICS}
              >
                + Add metric
              </Button>
            </div>

            {hero.metrics.length === 0 && (
              <div style={{
                padding: 12, border: `1px dashed ${COLOR.grayBorder}`, borderRadius: RADIUS.md,
                color: COLOR.textMuted, fontSize: 13, textAlign: 'center',
              }}>
                No metrics yet. Click <b>+ Add metric</b> to surface a trust signal.
              </div>
            )}

            {hero.metrics.map((m, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 1fr auto',
                gap: 8, alignItems: 'flex-end', marginTop: 8,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button size="sm" variant="ghost" onClick={() => moveMetric(i, -1)}
                    disabled={i === 0}                       title="Move up">▲</Button>
                  <Button size="sm" variant="ghost" onClick={() => moveMetric(i, +1)}
                    disabled={i === hero.metrics.length - 1} title="Move down">▼</Button>
                </div>
                <Field label={i === 0 ? 'Value' : ''}>
                  <Input
                    value={m.value}
                    onChange={e => setMetric(i, 'value', e.target.value)}
                    placeholder="12+"
                    maxLength={20}
                  />
                </Field>
                <Field label={i === 0 ? 'Label' : ''}>
                  <Input
                    value={m.label}
                    onChange={e => setMetric(i, 'label', e.target.value)}
                    placeholder="Years caring for our community"
                    maxLength={80}
                  />
                </Field>
                <Button size="sm" variant="danger" onClick={() => removeMetric(i)} title="Remove metric">✕</Button>
              </div>
            ))}
          </div>
        </SectionBlock>
      </Card>

      {/* ── Preview column ──────────────────────────────────────────── */}
      <HeroPreview hero={hero} />
    </div>
  );
}

// ── Live preview ────────────────────────────────────────────────────────
function HeroPreview({ hero }) {
  return (
    <Card style={{ position: 'sticky', top: 12, padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '8px 12px', background: COLOR.grayBg,
        borderBottom: `1px solid ${COLOR.graySubtle}`,
        fontSize: 11, color: COLOR.textMuted, textTransform: 'uppercase', letterSpacing: '.6px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>Live preview</span>
        <Badge color="gray">{hero.metrics.length} metric{hero.metrics.length === 1 ? '' : 's'}</Badge>
      </div>
      <div style={{
        padding: '24px 22px',
        background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
        minHeight: 260,
      }}>
        {hero.eyebrow && (
          <div style={{
            display: 'inline-block',
            fontSize: 11, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase',
            color: COLOR.primary || '#0e62a8',
            background: '#e7f1fb', padding: '4px 10px', borderRadius: 999, marginBottom: 12,
          }}>
            {hero.eyebrow}
          </div>
        )}
        <div style={{
          fontSize: 22, fontWeight: 800, lineHeight: 1.2, color: COLOR.text,
          marginBottom: 8,
        }}>
          {hero.title || <span style={{ color: COLOR.textMuted, fontWeight: 500 }}>Title goes here</span>}
        </div>
        {hero.subtitle && (
          <div style={{
            fontSize: 13, color: COLOR.textMuted, lineHeight: 1.55, marginBottom: 14,
          }}>
            {hero.subtitle}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hero.ctaPrimary.label && (
            <span style={{
              padding: '8px 14px', borderRadius: RADIUS.md, fontSize: 13, fontWeight: 600,
              background: COLOR.primary || '#0e62a8', color: '#fff',
            }}>{hero.ctaPrimary.label}</span>
          )}
          {hero.ctaSecondary.label && (
            <span style={{
              padding: '8px 14px', borderRadius: RADIUS.md, fontSize: 13, fontWeight: 600,
              border: `1px solid ${COLOR.grayBorder}`, color: COLOR.text, background: '#fff',
            }}>{hero.ctaSecondary.label}</span>
          )}
        </div>
        {hero.metrics.length > 0 && (
          <div style={{
            marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${COLOR.graySubtle}`,
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(hero.metrics.length, 4)}, minmax(0,1fr))`,
            gap: 10,
          }}>
            {hero.metrics.map((m, i) => (
              <div key={i} style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: COLOR.text }}>
                  {m.value || '—'}
                </div>
                <div style={{ fontSize: 11, color: COLOR.textMuted, lineHeight: 1.3 }}>
                  {m.label || '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Inline styles for fieldset blocks (CTA buttons) ──────────────────────
const fsStyle = {
  border: `1px solid ${COLOR.graySubtle}`,
  borderRadius: RADIUS.md,
  padding: '10px 12px 4px',
  margin: 0,
};
const legendStyle = {
  fontSize: 11, fontWeight: 700, color: COLOR.textMuted,
  textTransform: 'uppercase', letterSpacing: '.6px',
  padding: '0 6px',
};
